import { useState, useMemo } from 'react';
import {
    Button,
    Input,
    Loading,
    RadioGroup,
    Radio,
} from '@appkit4/react-components';
import ListingTable from '../../../shared/listingTable/ListingTable';
import DownloadModal from '../downloadModal/DownloadModal';
import ExportModal from '../exportModal/ExportModal';
import PDFViewerModal from '../../../shared/pdfViewerModal/PDFViewerModal';
import { useNavigate, useParams } from 'react-router-dom';
import './ProcessingTable.scss';
import { formatDate } from '../../../../helpers/helperFunctions';
import {
    usePostV1DocumentsDeleteDocument,
    usePostV1DocumentsDownloadDocuments,
} from '../../../../api/generated/endpoint';
import { DownloadDocumentsRequest } from '../../../../api/generated/models';
import { DeleteModal } from '../../../shared/deleteModal';
import Illustration6 from '../../../../assets/illustrations/Illustration_6.png';
import useToast from '../../../../helpers/useToast';
import useLocale from '../../../../locale/useLocale';
import DocumentTypeFilter from '../../../shared/documentTypeFilter/DocumentTypeFilter';

interface ProcessingTableProps {
    filteredData: any[];
    currentPage: number;
    pageSize: number;
    setCurrentPage: (page: number) => void;
    totalCount: number;
    isLoading?: boolean;
    activeTab?: number;
    filterDocTypes?: string[];
    onTabChange?: (index: number) => void;
    processName?: string;
    onDocumentUpdate?: () => void;
    processTypeId?: number;
}

const ProcessingTable = ({
    filteredData,
    currentPage,
    pageSize,
    setCurrentPage,
    isLoading = false,
    activeTab = 0,
    filterDocTypes,
    onTabChange,
    processName,
    onDocumentUpdate,
    processTypeId,
}: ProcessingTableProps) => {
    const [selectedIds, setSelectedIds] = useState<number[]>([]);
    const [isDownloadModalVisible, setIsDownloadModalVisible] = useState(false);
    const [isExportModalVisible, setIsExportModalVisible] = useState(false);
    const [isPdfModalVisible, setIsPdfModalVisible] = useState(false);
    const [selectedDocument, setSelectedDocument] = useState<number | null>(
        null
    );
    const [confirmationVisible, setConfirmationVisible] = useState(false);
    const [inputValue, setInputValue] = useState('');
    const [selectedDocumentId, setSelectedDocumentId] = useState<number>();
    const [selectedDocTypes, setSelectedDocTypes] = useState<string[]>(
        filterDocTypes || []
    );
    const [reportMisclassification, setReportMisclassification] =
        useState<boolean>(false);

    const { mutate: documentDownloadRequests, isLoading: isExporting } =
        usePostV1DocumentsDownloadDocuments();
    const [isDownloadLoading, setIsDownloadLoading] = useState(false);
    const { mutate: deleteDocument } = usePostV1DocumentsDeleteDocument();
    const showToast = useToast();
    const { l } = useLocale();
    const { processId } = useParams<{ processId: string }>();
    const navigate = useNavigate();

    const ValidationMapping: Record<string, number | number[]> = {
        'Awaiting Extraction': [2],
        'Awaiting Validation': [4, 9],
        'Validation Failed': [6],
        'Validation Low Confidence': [7],
        'Validation Passed': [8],
        'User Approved': [10],
        'PDF Generation Failed': [11],
        'PDF Generated': [12],
        'System Error': [1, 3, 5],
    };

    const getStatusByNumber = (num: number) => {
        for (const [key, value] of Object.entries(ValidationMapping)) {
            if (Array.isArray(value) ? value.includes(num) : value === num) {
                return key;
            }
        }
        return undefined;
    };

    const filterActionData = (data: any[]) => {
        return data.filter((row) => {
            const status = getStatusByNumber(row.processStatus);
            return (
                status === 'System Error' ||
                status === 'Validation Low Confidence' ||
                status === 'Validation Failed'
            );
        });
    };

    const getUniqueDocTypes = useMemo(() => {
        const types = Array.from(
            new Set(filteredData.map((item) => item.documentType))
        );
        return types.map((type) => ({ value: type, desc: type }));
    }, [filteredData]);

    const filterByDocType = (data: any[]) => {
        if (selectedDocTypes.length === 0) return data;
        return data.filter((item) =>
            selectedDocTypes.includes(item.documentType)
        );
    };

    const handleEdit = (row: any) => {
        navigate(`/process/${processId}/document/${row.documentId}`, {
            state: {
                documentId: row.documentId,
                parentLocation: `/process/${processId}/document`,
                fileName: row.parentDocument,
                parentName: processName,
                processingListingTab: activeTab,
                selectedDocTypes: selectedDocTypes,
            },
        });
    };

    const handleDelete = (row: any) => {
        setSelectedDocumentId(row.documentId);
        setConfirmationVisible(true);
    };

    const confirmDeletion = () => {
        deleteDocument(
            {
                data: {
                    documentIds: [Number(selectedDocumentId)],
                    reportMisclassification: reportMisclassification,
                },
            },
            {
                onSuccess: () => {
                    showToast(
                        l('_success'),
                        'Document has been deleted',
                        'success'
                    );
                    setConfirmationVisible(false);
                    setReportMisclassification(false);
                    setTimeout(() => {
                        if (onDocumentUpdate) {
                            onDocumentUpdate();
                        }
                    }, 3000);
                },
                onError: () => {
                    showToast(
                        l('_error'),
                        'Failed to delete document',
                        'error'
                    );
                },
            }
        );
    };

    const handleDownload = () => {
        setIsDownloadModalVisible(true);
    };

    const handleDownloadConfirm = (type: 'zip' | 'pdf') => {
        const docDownloadRequest: DownloadDocumentsRequest = {
            documentIds: selectedIds,
            downloadAsZip: type == 'zip',
        };

        setIsDownloadLoading(true);

        documentDownloadRequests(
            { data: docDownloadRequest },
            {
                onSuccess: async (response: any) => {
                    const link: any = document.createElement('a');
                    link.href = response.sasUrl;

                    const url = new URL(response.sasUrl);
                    const pathname = url.pathname;
                    const filename = pathname.substring(
                        pathname.lastIndexOf('/') + 1
                    );
                    link.download = filename || 'download';

                    document.body.appendChild(link);
                    link.click();
                    document.body.removeChild(link);
                    setIsDownloadLoading(false);
                },
                onError: (error) => {
                    console.log(error);
                    setIsDownloadLoading(false);
                },
            }
        );
    };

    const handleExport = () => {
        // If documents are selected and ProcessType is 2 or 3, export directly without modal
        if (
            (processTypeId === 2 || processTypeId === 3) &&
            selectedIds.length > 0
        ) {
            const docDownloadRequest: DownloadDocumentsRequest = {
                documentIds: selectedIds,
                downloadAsZip: true,
                processType: processTypeId,
                processId: processId ? parseInt(processId) : undefined,
            };

            documentDownloadRequests(
                { data: docDownloadRequest },
                {
                    onSuccess: async (response: any) => {
                        const link: any = document.createElement('a');
                        link.href = response.sasUrl;

                        const url = new URL(response.sasUrl);
                        const pathname = url.pathname;
                        const filename = pathname.substring(
                            pathname.lastIndexOf('/') + 1
                        );
                        link.download = filename || 'download';

                        document.body.appendChild(link);
                        link.click();
                        document.body.removeChild(link);
                    },
                    onError: (error) => {
                        console.log(error);
                        showToast(
                            l('_error'),
                            'Failed to download documents',
                            'error'
                        );
                    },
                }
            );
        } else {
            // Show export modal for data export
            setIsExportModalVisible(true);
        }
    };

    const handleExpand = (row: any) => {
        setSelectedDocument(row.documentId);
        setIsPdfModalVisible(true);
    };

    const tabs = [
        { label: 'All', value: 'all' },
        { label: 'Actions', value: 'actions' },
        { label: 'Complete', value: 'complete' },
    ];

    const columns = [
        {
            field: 'documentType',
            header: (
                <div className="document-type-header">
                    <span>Document Type</span>
                    <DocumentTypeFilter
                        onSelect={(value: any) => setSelectedDocTypes(value)}
                        options={getUniqueDocTypes}
                        selectedDocTypes={selectedDocTypes}
                    />
                </div>
            ),
            sortable: true,
        },
        {
            field: 'uploadedDate',
            header: 'Upload Date',
            renderCell: (row: any) => (
                <span>{formatDate(row.uploadedDate)}</span>
            ),
        },
        {
            field: 'parentDocument',
            header: 'Parent Document',
        },
        {
            field: 'startPage',
            header: 'Start Page',
        },
        {
            field: 'processStatus',
            header: 'Process Status',
            renderCell: (row: any) => {
                const getStatusIcon = (status: any) => {
                    switch (status) {
                        case 'Validation Failed':
                            return (
                                <span className="Appkit4-icon icon-warning-fill status-icon error-icon" />
                            );
                        case 'System Error':
                            return (
                                <span className="Appkit4-icon icon-warning-fill status-icon error-icon" />
                            );
                        case 'Awaiting Validation':
                            return (
                                <span className="status-dot pending-icon">
                                    ●
                                </span>
                            );
                        case 'Awaiting Extraction':
                            return (
                                <span className="status-dot pending-icon">
                                    ●
                                </span>
                            );
                        case 'Validation Passed':
                            return (
                                <span className="status-dot success-icon">
                                    ●
                                </span>
                            );
                        case 'Validation Low Confidence':
                            return (
                                <span className="Appkit4-icon icon-warning-fill custom-warning-icon status-icon warning-icon" />
                            );
                        case 'PDF Generated':
                            return (
                                <span className="status-dot success-icon">
                                    ●
                                </span>
                            );
                        case 'User Approved':
                            return (
                                <span className="status-dot success-icon">
                                    ●
                                </span>
                            );
                        case 'PDF Generation Failed':
                            return (
                                <span className="Appkit4-icon icon-warning-fill status-icon error-icon" />
                            );
                        default:
                            return null;
                    }
                };

                return (
                    <div className="d-flex align-items-center gap-2">
                        {getStatusIcon(getStatusByNumber(row.processStatus))}
                        <span>{getStatusByNumber(row.processStatus)}</span>
                    </div>
                );
            },
        },
        {
            field: 'lastUpdated',
            header: 'Last Updated',
            renderCell: (row: any) => (
                <span>{formatDate(row.lastUpdated)}</span>
            ),
        },
        {
            field: 'documentType',
            header: 'Edit',
            style: { width: '50px' },
            renderCell: (row: any) => {
                const statusText = getStatusByNumber(row.processStatus);

                if (
                    statusText !== 'System Error' &&
                    statusText !== 'Awaiting Validation' &&
                    statusText !== 'Awaiting Extraction'
                ) {
                    return (
                        <span
                            className="Appkit4-icon icon-edit-outline action-icon"
                            role="button"
                            onClick={() => handleEdit(row)}
                            aria-label="Edit"
                        />
                    );
                } else {
                    return (
                        <span
                            className="Appkit4-icon icon-edit-outline action-icon-disabled"
                            role="button"
                            aria-label="Edit"
                        />
                    );
                }
            },
        },
        {
            field: 'documentType',
            header: 'Delete',
            style: { width: '50px' },
            renderCell: (row: any) => (
                <span
                    className="Appkit4-icon icon-delete-outline action-icon"
                    role="button"
                    onClick={() => handleDelete(row)}
                    aria-label="Delete"
                />
            ),
        },
    ];

    const handleTabChange = (index: number) => {
        if (onTabChange) {
            onTabChange(index);
        }
        // Update URL state when tab changes
        navigate(`/process/${processId}/document`, {
            state: { processingListingTab: index },
            replace: true,
        });
    };

    if (isLoading) {
        return (
            <div className="processing-table loading-state">
                <Loading loadingType="circular" indeterminate compact={false} />
            </div>
        );
    }

    const deleteModalContent = () => (
        <div className="del-modal-content-wrapper">
            <img
                src={Illustration6}
                alt="Delete Confirmation"
                className="del-modal-image"
            />
            <p>Are you sure you want to delete this document?</p>
            <Input
                type="text"
                placeholder="Delete"
                className="del-folder-confirmation-input"
                value={inputValue}
                onChange={(value: string) => setInputValue(value)}
            />
            <p style={{ fontSize: 14 }}>
                Type “Delete” To Delete This Document
            </p>
            <div style={{ marginTop: '20px' }}>
                <p
                    id="misclassification-radio-group-title"
                    style={{ marginBottom: '10px' }}
                >
                    Report as Misclassification
                </p>
                <RadioGroup
                    aria-labelledby="misclassification-radio-group-title"
                    name="misclassification"
                    value={reportMisclassification.toString()}
                    onChange={(value: string) =>
                        setReportMisclassification(value === 'true')
                    }
                >
                    <Radio value="true">Yes</Radio>
                    <Radio value="false">No</Radio>
                </RadioGroup>
            </div>
            <Button
                onClick={confirmDeletion}
                kind="danger"
                disabled={inputValue.toLowerCase() !== 'delete'}
                style={{ marginTop: '40px' }}
            >
                Delete
            </Button>
        </div>
    );

    return (
        <div className="processing-table">
            <ListingTable
                tabs={tabs}
                activeTab={activeTab}
                columns={columns}
                data={filterByDocType(
                    activeTab === 1
                        ? filterActionData(filteredData)
                        : filteredData
                )}
                totalCount={
                    filterByDocType(
                        activeTab === 1
                            ? filterActionData(filteredData)
                            : filteredData
                    ).length
                }
                currentPage={currentPage}
                pageSize={pageSize}
                onPageChange={setCurrentPage}
                onTabChange={handleTabChange}
                selectedIndexs={selectedIds}
                onSelectedChange={setSelectedIds}
                checkableColumn={{
                    field: 'documentId',
                    header: 'Document ID',
                    renderCell: (row) => (
                        <div className="d-flex align-items-center gap-2">
                            <span>{row.documentId}</span>
                            <span
                                className="Appkit4-icon icon-open-in-new-window-outline window-icon"
                                role="button"
                                aria-label="Window"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    handleExpand(row);
                                }}
                            />
                        </div>
                    ),
                }}
                headerButtons={
                    <div className="table-header-buttons">
                        {processTypeId !== 2 && (
                            <Button
                                kind="secondary"
                                icon="icon-download-outline"
                                onClick={handleDownload}
                                disabled={selectedIds.length === 0}
                                loading={isDownloadLoading}
                            >
                                Download document ({selectedIds.length})
                            </Button>
                        )}
                        <Button
                            kind="primary"
                            icon="icon-document-export-outline"
                            onClick={handleExport}
                            loading={isExporting}
                            disabled={isExporting}
                        >
                            {(processTypeId === 2 || processTypeId === 3) &&
                            selectedIds.length > 0
                                ? `Export documents (${selectedIds.length})`
                                : 'Export data'}
                        </Button>
                    </div>
                }
            />

            <DownloadModal
                visible={isDownloadModalVisible}
                onClose={() => setIsDownloadModalVisible(false)}
                onDownload={handleDownloadConfirm}
            />

            <ExportModal
                visible={isExportModalVisible}
                onClose={() => setIsExportModalVisible(false)}
                processId={processId}
                processTypeId={processTypeId}
                processName={processName}
            />

            {selectedDocument && (
                <PDFViewerModal
                    visible={isPdfModalVisible}
                    onClose={() => {
                        setIsPdfModalVisible(false);
                        setSelectedDocument(null);
                    }}
                    documentId={selectedDocument}
                />
            )}
            <DeleteModal
                content={deleteModalContent()}
                visible={confirmationVisible}
                onClose={() => setConfirmationVisible(false)}
                title="Delete Document"
            />
        </div>
    );
};

export default ProcessingTable;
